import { rootApi } from '@components/rootApi';
import {
  campaignsApi as campaignsApiV3,
  GetCampaignsParams,
} from '@features/campaigns';
import { ReportType } from '../hooks/useCurrentReportType';
import {
  DimensionMenuPayload,
  DimensionPayload,
  Payload,
} from '../SalesAttribution/salesAttributionSlice';
import { CampaignPayload, campaignPayloadSchema } from './schemas/campaign';
import {
  Lci3Report,
  lci3ReportSchema,
  lci3ReportsSchema,
} from './schemas/lci3Reports';
import { TAGS } from './tags';

export type CampaignParams = { id: number | string } & Partial<{
  type: ReportType;
  report_group: string;
  report_id: number | string;
  channel_partner: string;
  includeUnapproved: boolean;
  showLci3Reports: boolean;
}>;

type UpdateLci3ReportParams = {
  campaignId: number;
  id: number;
  status?: 'approved' | 'pending';
  salesReportStatus?: 'approved' | 'pending';
  campaignsParams?: GetCampaignsParams;
  reportsParams?: GetLci3ReportParams;
  isLatestReport?: boolean;
  channel?: string;
};

export type GetLci3ReportParams = Pick<
  CampaignParams,
  'id' | 'includeUnapproved'
> & { channel?: string; show_restricted_values?: boolean };

const pathWithBaseUrl = (path: string) => `/api/${path}`;

const lci3ReportId = (id: number, channel?: string) => [id, channel].join('-');

const campaignsApi = rootApi.injectEndpoints({
  endpoints: builder => ({
    getCampaign: builder.query<CampaignPayload, CampaignParams>({
      query: params => {
        const { id, ...queryParams } = params;
        return { url: pathWithBaseUrl(`campaigns/${id}`), params: queryParams };
      },
      transformResponse: (response: CampaignPayload) =>
        campaignPayloadSchema.parse(response),
      providesTags: result => [
        {
          type: 'Campaigns',
          id: result?.id,
        },
      ],
    }),
    getRsaSummary: builder.query<Payload, CampaignParams>({
      query: params => {
        const { id, ...queryParams } = params;
        return {
          url: pathWithBaseUrl(`campaigns/${id}/sales_attribution/summary`),
          params: queryParams,
        };
      },
    }),
    getRsaDimensionMenu: builder.query<
      DimensionMenuPayload,
      {
        campaignId: number | string;
        channelPartner?: string;
        showLci3Reports?: boolean;
      }
    >({
      query: params => {
        const { campaignId, channelPartner, showLci3Reports } = params;
        return {
          url: pathWithBaseUrl(
            `campaigns/${campaignId}/sales_attribution/menu`
          ),
          params: { channel_partner: channelPartner, showLci3Reports },
        };
      },
    }),
    getRsaDimension: builder.query<
      DimensionPayload,
      {
        campaignId: number | string;
        dimensionSlug?: string;
        channelPartner?: string;
        showLci3Reports?: boolean;
      }
    >({
      query: params => {
        const { campaignId, dimensionSlug, channelPartner, showLci3Reports } =
          params;
        return {
          url: pathWithBaseUrl(
            `campaigns/${campaignId}/sales_attribution/dimensions/${
              dimensionSlug ?? ''
            }`
          ),
          params: { channel_partner: channelPartner, showLci3Reports },
        };
      },
    }),
    submitSalesAttributionRequest: builder.mutation<void, string | number>({
      query: campaignId => ({
        url: pathWithBaseUrl('sales_attribution_submissions'),
        method: 'POST',
        body: { campaign_id: campaignId },
      }),
    }),
    updateCampaignAdSpend: builder.mutation<
      void,
      { id: number; ad_spend: number }
    >({
      query: ({ id, ad_spend }) => ({
        url: pathWithBaseUrl(`campaigns/${id}/ad_spend`),
        method: 'POST',
        body: { ad_spend },
      }),
      invalidatesTags: (_, __, params) => [
        { type: 'Campaigns', id: params.id },
      ],
    }),
    schedulePancakeDownload: builder.mutation<void, CampaignParams>({
      query: ({ id, ...body }) => ({
        url: pathWithBaseUrl(`campaigns/${id}/schedule_pancake_download`),
        method: 'PATCH',
        body,
      }),
    }),
    scheduleSummaryDownload: builder.mutation<void, CampaignParams>({
      query: ({ id, ...body }) => ({
        url: pathWithBaseUrl(`campaigns/${id}/schedule_download`),
        method: 'PATCH',
        body,
      }),
    }),
    getLci3Reports: builder.query<Lci3Report[], GetLci3ReportParams>({
      query: params => {
        const { id, ...queryParams } = params;
        return {
          url: pathWithBaseUrl(`v3/campaigns/${id}/reports`),
          params: queryParams,
        };
      },
      transformResponse: (response: Lci3Report[]) =>
        lci3ReportsSchema.parse(response),
      providesTags: (result, _error, params) =>
        result?.map(({ id }) => ({
          type: TAGS.lci3Reports,
          id: lci3ReportId(id, params.channel),
        })) ?? [],
    }),
    updateLci3Report: builder.mutation<void, UpdateLci3ReportParams>({
      query: ({ campaignId, id, ...body }) => ({
        url: pathWithBaseUrl(`v3/campaigns/${campaignId}/reports/${id}`),
        method: 'PATCH',
        body,
      }),
      async onQueryStarted(params, { dispatch, queryFulfilled }) {
        const { campaignsParams, reportsParams } = params;

        const getLci3ReportsResult = dispatch(
          campaignsApi.util.updateQueryData(
            'getLci3Reports',
            {
              id: params.campaignId,
              includeUnapproved: true,
              show_restricted_values: true,
              channel: params.channel,
              ...reportsParams,
            },
            draft => {
              const reportIndex = draft.findIndex(r => r.id === params.id);

              const report = draft[reportIndex];

              if (!report) return;

              if (params.status) {
                report.status = params.status;
              }

              if (params.salesReportStatus) {
                report.salesReportStatus = params.salesReportStatus;
              }
            }
          )
        );

        const getCampaignsResult = campaignsParams
          ? [
              dispatch(
                campaignsApiV3.util.updateQueryData(
                  'getCampaigns',
                  campaignsParams,
                  draft => {
                    if (!params.isLatestReport) return;

                    const campaign = draft.campaigns.find(
                      c => c.id === params.campaignId
                    );
                    if (!campaign) return;

                    if (params.status) {
                      campaign.latestReportStatus = params.status;
                    }
                  }
                )
              ),
            ]
          : [];

        const results = [getLci3ReportsResult, ...getCampaignsResult];

        try {
          await queryFulfilled;
        } catch (e) {
          console.error(e);
          results.forEach(result => result.undo());
        }
      },
    }),

    getCustomReports: builder.query<Lci3Report[], CampaignParams>({
      query: params => {
        const { id, ...queryParams } = params;
        return {
          url: pathWithBaseUrl(`v3/campaigns/${id}/custom_reports`),
          params: queryParams,
        };
      },
      transformResponse: (response: Lci3Report[]) =>
        lci3ReportsSchema.parse(response),
    }),
    getCustomReport: builder.query<Lci3Report, GetLci3ReportParams>({
      query: ({ id, ...params }) => {
        return {
          url: pathWithBaseUrl(`v3/custom_reports/${id}`),
          params,
        };
      },
      transformResponse: (response: Lci3Report) =>
        lci3ReportSchema.parse(response),
    }),
    updateCustomReport: builder.mutation<void, UpdateLci3ReportParams>({
      query: ({ campaignId, id, ...body }) => ({
        url: pathWithBaseUrl(`v3/campaigns/${campaignId}/custom_reports/${id}`),
        method: 'PATCH',
        body,
      }),
      async onQueryStarted(params, { dispatch, queryFulfilled }) {
        const getCustomReportsResult = dispatch(
          campaignsApi.util.updateQueryData(
            'getCustomReports',
            { id: params.campaignId, includeUnapproved: true },
            draft => {
              const reportIndex = draft.findIndex(r => r.id === params.id);
              const report = draft[reportIndex];

              if (!report) return;

              if (params.status) {
                report.status = params.status;
              }
            }
          )
        );

        try {
          await queryFulfilled;
        } catch (e) {
          console.error(e);
          getCustomReportsResult.undo();
        }
      },
    }),
    updateReports: builder.mutation<
      void,
      Pick<UpdateLci3ReportParams, 'status' | 'campaignId'> &
        Pick<Lci3Report, 'id'> & { channels: string[] }
    >({
      query: ({ campaignId, ...body }) => ({
        url: pathWithBaseUrl(`v3/campaigns/${campaignId}/reports`),
        method: 'PATCH',
        body,
      }),
      invalidatesTags: (_, __, params) =>
        params.channels.map(channel => ({
          type: TAGS.lci3Reports,
          id: lci3ReportId(params.id, channel),
        })),
    }),
  }),
});

export const {
  useGetCampaignQuery,
  useSubmitSalesAttributionRequestMutation,

  useSchedulePancakeDownloadMutation,
  useScheduleSummaryDownloadMutation,
  useGetLci3ReportsQuery,
  useUpdateLci3ReportMutation,
  useGetCustomReportsQuery,
  useUpdateCustomReportMutation,
  useGetCustomReportQuery,
  useUpdateReportsMutation,
} = campaignsApi;
